/**
 * Keeping rows with a blank value inside a group instead of loose at the root.
 *
 * A null grouping key is skipped rather than grouped, so a field carrying null or '' yields
 * a mix of real groups and loose rows. The chart renders only what sits at depth 0 and takes
 * its shape from there, so the loose rows win and the groups are dropped.
 *
 * `groupingValueGetter` runs when the key is computed, so coalescing blanks to a label keeps
 * the key non-null and the row in a real group — one honest `(Blank)` slice with its total.
 */

import type { GridColDef } from '@mui/x-data-grid-premium';

/** The group blank values collect under, in both the grid and the chart. */
export const BLANK_GROUP_LABEL = '(Blank)';

/**
 * Column types this applies to.
 *
 * Grouping keys are string, number or boolean, so these two coalesce cleanly. Dates key on a
 * `Date`, and a raw timestamp is a cardinality problem rather than a blank-value one.
 */
const COALESCED_TYPES = new Set(['string', 'number']);

function isBlank(value: unknown): boolean {
    return value === null || value === undefined || value === '';
}

/** Adds the blank-coalescing getter, leaving any column that defines its own alone. */
export function withBlankGroupingKeys(columns: GridColDef[]): GridColDef[] {
    return columns.map((column) => {
        if (column.groupingValueGetter || !COALESCED_TYPES.has(column.type ?? 'string')) {
            return column;
        }

        return {
            ...column,
            groupingValueGetter: (value: string | number | null | undefined) =>
                isBlank(value) ? BLANK_GROUP_LABEL : value,
        };
    });
}
